import { useEffect, useRef, useState, Fragment } from "react";
import { toast } from "sonner";
import { api, API, TOKEN_KEY } from "../../lib/api";
import { UserCircle, UploadSimple, CircleNotch, Plus, Trash, PencilSimple, IdentificationCard, CaretDown, CaretRight, CheckCircle, Eye } from "@phosphor-icons/react";
import { DocViewerModal, LockedBadge, isDocLocked } from "../../components/DocViewer";

function fileUrl(id) {
  return id ? `${API}/files/${id}/download?auth=${localStorage.getItem(TOKEN_KEY)}` : null;
}

async function uploadRaw(file) {
  const fd = new FormData();
  fd.append("file", file);
  const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return up.data.id;
}

const EMPTY = { rut: "", name: "", lastName: "", birthDate: "", gender: "", photoFileId: null, licenseClass: "", licenseExpiry: "", healthDeclaration: false };
const DOC_STATUS = {
  PENDIENTE: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
  APROBADO: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-700" },
  RECHAZADO: { label: "Rechazado", cls: "bg-red-100 text-red-700" },
};

export default function CarrierDrivers() {
  const [drivers, setDrivers] = useState(null);
  const [docs, setDocs] = useState([]);
  const [config, setConfig] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [viewFile, setViewFile] = useState(null);

  const load = () => api.get("/carrier/profile").then((r) => { setDrivers(r.data.drivers || []); setDocs(r.data.documents || []); }).catch(() => setDrivers([]));
  useEffect(() => { load(); api.get("/meta/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (d) => { setForm({ ...EMPTY, ...d }); setEditing(d.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const id = await uploadRaw(file); setForm((f) => ({ ...f, photoFileId: id })); toast.success("Foto cargada"); }
    catch { toast.error("Error al subir la foto"); }
    setBusy(false);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Indica el nombre del conductor");
    setBusy(true);
    try {
      const body = {
        rut: form.rut, name: form.name, lastName: form.lastName, birthDate: form.birthDate,
        gender: form.gender, photoFileId: form.photoFileId, licenseClass: form.licenseClass,
        licenseExpiry: form.licenseExpiry, healthDeclaration: !!form.healthDeclaration,
      };
      if (editing === "new") await api.post("/carrier/drivers", body);
      else await api.put(`/carrier/drivers/${editing}`, body);
      toast.success("Conductor guardado");
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este conductor?")) return;
    try { await api.delete(`/carrier/drivers/${id}`); toast.success("Conductor eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const uploadDoc = async (driverId, type, expiry, file) => {
    if (!file) return;
    setUploading(`${driverId}:${type}`);
    try {
      const fileId = await uploadRaw(file);
      await api.post("/carrier/documents", { documentType: type, fileId, expirationDate: expiry || "", driverId });
      toast.success("Documento cargado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
    setUploading(null);
  };

  if (!drivers) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const reqDocs = config.driverDocs || [];
  const requiredSet = new Set((config.requiredDocs || []).filter((t) => reqDocs.includes(t)));
  const docSummary = (id) => {
    const own = docs.filter((d) => d.driverId === id);
    const approved = [...requiredSet].filter((t) => own.some((d) => d.documentType === t && d.status === "APROBADO")).length;
    return { approved, total: requiredSet.size };
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8" data-testid="carrier-drivers">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Acreditación</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Conductores</h1>
          <p className="text-sm text-zinc-500 mt-1">Registra a los conductores de tu empresa y sus documentos obligatorios.</p>
        </div>
        <button onClick={openNew} data-testid="add-driver-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF5A36] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 shrink-0">
          <Plus size={18} weight="bold" /> Agregar conductor
        </button>
      </div>

      {drivers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="drivers-empty">
          Aún no registras conductores. Agrega al menos uno para poder ofertar servicios.
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="drivers-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-zinc-400 bg-zinc-50 border-b border-zinc-200">
                <th className="px-4 py-3 font-bold w-8"></th>
                <th className="px-4 py-3 font-bold">Conductor</th>
                <th className="px-4 py-3 font-bold">RUT</th>
                <th className="px-4 py-3 font-bold">Licencia</th>
                <th className="px-4 py-3 font-bold">Género</th>
                <th className="px-4 py-3 font-bold">Nacimiento</th>
                <th className="px-4 py-3 font-bold">Documentos</th>
                <th className="px-4 py-3 font-bold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => {
                const sum = docSummary(d.id);
                const complete = sum.total > 0 && sum.approved === sum.total;
                const open = !!expanded[d.id];
                return (
                  <Fragment key={d.id}>
                    <tr className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`driver-row-${d.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => toggle(d.id)} data-testid={`driver-expand-${d.id}`} className="text-zinc-500 hover:text-[#FF5A36]">
                          {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full border border-zinc-200 bg-zinc-50 overflow-hidden flex items-center justify-center shrink-0">
                            {d.photoFileId ? <img src={fileUrl(d.photoFileId)} alt={d.name} className="w-full h-full object-cover" /> : <UserCircle size={26} className="text-zinc-300" />}
                          </div>
                          <span className="font-semibold whitespace-nowrap">{d.name} {d.lastName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-600">{d.rut || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">{d.licenseClass || "—"}{d.licenseExpiry ? ` · ${String(d.licenseExpiry).slice(0,10)}` : ""}</td>
                      <td className="px-4 py-3 text-zinc-600">{d.gender || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">{d.birthDate ? String(d.birthDate).slice(0,10) : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${complete ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {complete && <CheckCircle size={12} weight="fill" />}{sum.approved}/{sum.total} aprobados
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(d)} data-testid={`edit-driver-${d.id}`} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600"><PencilSimple size={16} /></button>
                          <button onClick={() => del(d.id)} data-testid={`del-driver-${d.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-zinc-50/60" data-testid={`driver-docs-${d.id}`}>
                        <td colSpan={8} className="px-4 py-4">
                          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Documentos del conductor</div>
                          <table className="w-full text-sm bg-white rounded-xl border border-zinc-200 overflow-hidden">
                            <thead>
                              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-400 border-b border-zinc-100">
                                <th className="px-3 py-2 font-bold">Documento</th>
                                <th className="px-3 py-2 font-bold">Vencimiento</th>
                                <th className="px-3 py-2 font-bold">Estado</th>
                                <th className="px-3 py-2 font-bold text-right">Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reqDocs.map((type) => (
                                <DriverDocRow key={type} driverId={d.id} type={type}
                                  required={(config.requiredDocs || []).includes(type)}
                                  needsExpiry={(config.docsWithExpiry || []).includes(type)}
                                  docs={docs.filter((x) => x.driverId === d.id)}
                                  uploading={uploading} onUpload={uploadDoc}
                                  onView={(fid) => setViewFile({ id: fid, title: type })} />
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="driver-form-modal">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2"><IdentificationCard size={22} className="text-[#FF5A36]" /> {editing === "new" ? "Nuevo conductor" : "Editar conductor"}</h2>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-20 h-20 rounded-full border border-zinc-200 bg-zinc-50 overflow-hidden flex items-center justify-center shrink-0">
                {form.photoFileId ? <img src={fileUrl(form.photoFileId)} alt="Foto" className="w-full h-full object-cover" /> : <UserCircle size={44} className="text-zinc-300" />}
              </div>
              <label className="text-sm font-semibold text-[#FF5A36] cursor-pointer inline-flex items-center gap-1 hover:underline" data-testid="driver-photo-upload">
                <UploadSimple size={16} /> {form.photoFileId ? "Cambiar foto" : "Subir foto"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <DField label="Nombre" testid="driver-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <DField label="Apellido" testid="driver-lastname" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} />
              <DField label="RUT" testid="driver-rut" value={form.rut} onChange={(v) => setForm({ ...form, rut: v })} placeholder="12.345.678-9" />
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Fecha de nacimiento</label>
                <input type="date" data-testid="driver-birthdate" value={(form.birthDate || "").slice(0, 10)} onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
                  className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Género</label>
                <select data-testid="driver-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                  <option value="">Selecciona</option>
                  <option>Masculino</option><option>Femenino</option><option>Otro</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Clase de licencia</label>
                <select data-testid="driver-license-class" value={form.licenseClass} onChange={(e) => setForm({ ...form, licenseClass: e.target.value })}
                  className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                  <option value="">Selecciona</option>
                  {(config.licenseClasses || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Vencimiento licencia</label>
                <input type="date" data-testid="driver-license-expiry" value={(form.licenseExpiry || "").slice(0, 10)} onChange={(e) => setForm({ ...form, licenseExpiry: e.target.value })}
                  className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
              </div>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input type="checkbox" data-testid="driver-health" checked={!!form.healthDeclaration} onChange={(e) => setForm({ ...form, healthDeclaration: e.target.checked })} className="w-4 h-4 accent-[#FF5A36]" />
              Declara no padecer enfermedades inhabilitantes para conducir (Ley Jacinta)
            </label>
            <div className="flex gap-2 mt-5">
              <button onClick={close} className="flex-1 py-3 rounded-xl border border-zinc-300 font-semibold hover:bg-zinc-50">Cancelar</button>
              <button onClick={save} disabled={busy} data-testid="driver-save"
                className="flex-1 py-3 rounded-xl bg-[#FF5A36] text-white font-bold hover:bg-[#E04B2A] disabled:opacity-60 active:scale-95">
                {busy ? "Guardando…" : "Guardar conductor"}
              </button>
            </div>
          </div>
        </div>
      )}

      <DocViewerModal fileId={viewFile?.id} title={viewFile?.title} onClose={() => setViewFile(null)} />
    </div>
  );
}

function DField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1.5 w-full h-11 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
    </div>
  );
}

function DriverDocRow({ driverId, type, required, needsExpiry, docs, uploading, onUpload, onView }) {
  const [expiry, setExpiry] = useState("");
  const fileRef = useRef(null);
  const doc = docs.filter((d) => d.documentType === type).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0];
  const st = doc ? (DOC_STATUS[doc.status] || DOC_STATUS.PENDIENTE) : null;
  const busy = uploading === `${driverId}:${type}`;
  const locked = isDocLocked(doc);
  return (
    <tr className="border-b border-zinc-50 last:border-0" data-testid={`driver-doc-${driverId}-${type}`}>
      <td className="px-3 py-2">
        <span className="font-medium">{type}</span>
        {required && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-[#E04B2A]">Obligatorio</span>}
        {doc?.status === "RECHAZADO" && doc.rejectionReason && <div className="text-xs text-red-600 mt-0.5">Motivo: {doc.rejectionReason}</div>}
      </td>
      <td className="px-3 py-2">
        {needsExpiry ? (
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid={`driver-doc-expiry-${driverId}-${type}`} disabled={locked}
            className="h-9 px-2 rounded-lg border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5A36] disabled:bg-zinc-50" />
        ) : <span className="text-zinc-400">—</span>}
      </td>
      <td className="px-3 py-2">
        {st ? <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
          : <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">Falta</span>}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex items-center gap-2 justify-end">
          {doc?.fileId && (
            <button onClick={() => onView(doc.fileId)} data-testid={`driver-doc-view-${driverId}-${type}`}
              className="h-9 px-3 rounded-lg border border-zinc-300 text-sm font-semibold inline-flex items-center gap-1.5 hover:border-[#FF5A36] transition-colors">
              <Eye size={16} /> Ver
            </button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
            onChange={(e) => { const f = e.target.files[0]; if (needsExpiry && !expiry) { toast.error("Indica la fecha de vencimiento primero"); e.target.value = ""; return; } onUpload(driverId, type, expiry, f); e.target.value = ""; }} />
          {locked ? <LockedBadge /> : (
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`driver-doc-upload-${driverId}-${type}`}
              className="h-9 px-4 rounded-lg border border-dashed border-zinc-400 text-sm font-semibold inline-flex items-center gap-1.5 hover:border-[#FF5A36] transition-colors disabled:opacity-60">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} {doc ? "Reemplazar" : "Subir"}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
